/**
 * SessionIdentityCard — the profile/self identity block. Reads the resolved session view (identity
 * from the auth store, degrade-aware). Falls back to decoded token claims for id/lab when the iam
 * identity surface has not yet populated the display fields (email/nomComplet blank). Mono ids stay
 * LTR inside an RTL document via `nv-mono nv-ltr-island`.
 */
import { Badge, Card } from "@/ui";
import { useT } from "@/app";
import type { SessionView } from "../useSession";
import { useTokenClaims } from "../useSession";

export interface SessionIdentityCardProps {
  session: SessionView;
}

export function SessionIdentityCard({ session }: SessionIdentityCardProps) {
  const { t } = useT();
  const claims = useTokenClaims();

  const userId = session.id || claims?.utilisateurId || t("auth.session.none");
  const labId = session.labId ?? session.laboratoireId ?? claims?.laboratoireId ?? t("auth.session.none");
  const labLabel = session.labNom ?? labId;

  return (
    <Card title={t("auth.session.identity")}>
      <dl
        style={{
          display: "grid",
          gridTemplateColumns: "max-content 1fr",
          gap: "var(--nv-s-3) var(--nv-s-5)",
          margin: 0,
          alignItems: "baseline",
        }}
      >
        <dt style={{ color: "var(--nv-slate)" }}>{t("auth.session.field.name")}</dt>
        <dd style={{ margin: 0 }}>{session.nomComplet || t("auth.session.none")}</dd>

        <dt style={{ color: "var(--nv-slate)" }}>{t("auth.session.field.email")}</dt>
        <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
          {session.email || t("auth.session.none")}
        </dd>

        <dt style={{ color: "var(--nv-slate)" }}>{t("auth.session.field.user")}</dt>
        <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
          {userId}
        </dd>

        <dt style={{ color: "var(--nv-slate)" }}>{t("auth.session.field.lab")}</dt>
        <dd className="nv-mono nv-ltr-island" style={{ margin: 0 }}>
          {labLabel}
        </dd>

        <dt style={{ color: "var(--nv-slate)" }}>{t("auth.session.field.owner")}</dt>
        <dd style={{ margin: 0 }}>
          <Badge tone={session.estProprietaire ? "info" : "neutral"}>
            {session.estProprietaire
              ? t("auth.session.field.owner.yes")
              : t("auth.session.field.owner.no")}
          </Badge>
        </dd>

        <dt style={{ color: "var(--nv-slate)" }}>{t("auth.session.field.presets")}</dt>
        <dd style={{ margin: 0, display: "flex", flexWrap: "wrap", gap: "var(--nv-s-2)" }}>
          {session.presets.length > 0 ? (
            session.presets.map((p) => (
              <Badge key={p} tone="neutral">
                {p}
              </Badge>
            ))
          ) : (
            <span style={{ color: "var(--nv-steel)" }}>{t("auth.session.none")}</span>
          )}
        </dd>
      </dl>
    </Card>
  );
}
